import React from 'react';
import App from 'next/app';
import 'stylesheets/globals/base.scss';
import smoothscroll from 'smoothscroll-polyfill';
import {FlagServiceProvider, initializeFlagService} from 'utils/flag-service';
import {NextIntlClientProvider} from 'next-intl';
import {LOCALES} from 'helpers/i18n';
import {onIntlError} from 'helpers/intlOnError';
import {hasModelContext, safeRegisterTool} from 'components/webmcp/utils';
import DatadogInit from '../utils/datadog';
import setupChunkErrorReload from '../utils/chunkErrorReload';

const flagServiceInstance = initializeFlagService();
flagServiceInstance.init({
  steaming: true
});

// Registered at module scope (not componentDidMount) so it's listening before hydration,
// otherwise a chunk that fails while Next is still bootstrapping goes unhandled.
if (typeof window !== 'undefined') {
  setupChunkErrorReload();
}

function registerWebMCPGlobalTools() {
  if (!hasModelContext()) return;

  safeRegisterTool({
    name: 'get_sitemap',
    description: 'Return all published page URLs on workable.com from /sitemap.xml.',
    inputSchema: {type: 'object', properties: {}},
    execute: async () => {
      const r = await fetch('/sitemap.xml');
      return r.text();
    }
  });

  safeRegisterTool({
    name: 'get_llms_index',
    description: 'Return the curated site index from /llms.txt, optimised for LLM and agent clients.',
    inputSchema: {type: 'object', properties: {}},
    execute: async () => {
      const r = await fetch('/llms.txt');
      return r.text();
    }
  });
}

export default class Workable extends App {
  componentDidMount() {
    smoothscroll.polyfill();
    DatadogInit();
    registerWebMCPGlobalTools();
  }

  render() {
    const {Component, pageProps} = this.props;
    const {messages, locale = LOCALES.DEFAULT} = pageProps;

    // No global messages fallback — each page must set messages via getStaticProps
    // (pageMessagesProps, getSharedPageStaticProps, or getEmptyPageStaticProps).
    if (messages == null) {
      throw new Error(
        '[i18n] pageProps.messages is required. Export getStaticProps with pageMessagesProps, getSharedPageStaticProps, or getEmptyPageStaticProps.'
      );
    }

    return (
      <FlagServiceProvider flagServiceInstance={flagServiceInstance}>
        <NextIntlClientProvider
          locale={locale}
          messages={messages}
          timeZone="UTC"
          onError={onIntlError}
        >
          <Component {...pageProps} />
        </NextIntlClientProvider>
      </FlagServiceProvider>
    );
  }
}
